/**
 * Karbon360° v2 — topbar: breadcrumb trail, ⌘K launcher, reporting-period
 * switcher and the account menu.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from './Icons';
import { useT, LANGUAGES } from '../../i18n';
import { crumbsFor } from './nav-model';
import { useReportingPeriod } from './ReportingPeriod';
import { useSession, clearSession } from './session';

/** Close a popover on outside click or Escape. */
function useOutside(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return ref;
}

export default function TopbarV2({ onOpenPalette }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const crumbs = crumbsFor(location.pathname);
  const { period, setPeriod, options } = useReportingPeriod();

  const [pOpen, setPOpen] = useState(false);
  const [uOpen, setUOpen] = useState(false);
  const pRef = useOutside(() => setPOpen(false));
  const uRef = useOutside(() => setUOpen(false));

  // Role comes from the server (users.role), not from a hardcoded email list and
  // not from a JWT claim — so revoking admin takes effect on the next load.
  const session = useSession();
  const email = session.email || '';
  const { isAdmin, isOwner } = session;
  const plan = session.subscription_plan || '';

  const initials = (email || 'K3')
    .split('@')[0]
    .split(/[.\-_]/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('') || 'K3';

  const logout = () => {
    try { localStorage.removeItem('token'); } catch { /* ignore */ }
    clearSession();
    navigate('/');
  };

  return (
    <header className="k2-top">
      <nav className="k2-bc" aria-label={t('chrome.breadcrumb')}>
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c}-${i}`}>
            {i > 0 && <X.Chev s={13} />}
            {i === crumbs.length - 1 ? <b>{t(c)}</b> : <span>{t(c)}</span>}
          </React.Fragment>
        ))}
      </nav>

      <span className="k2-sp" />

      <button type="button" className="k2-kbd-btn" onClick={onOpenPalette} aria-label={t('chrome.search')}>
        <X.Search s={14} />
        <span>{t('chrome.search')}</span>
        <span className="k2-kbd">⌘K</span>
      </button>

      <span className="k2-vsep" />

      <div style={{ position: 'relative' }} ref={pRef}>
        <button type="button" className="k2-period" onClick={() => setPOpen((v) => !v)} aria-expanded={pOpen}>
          <span className="k2-dot" />
          <span>{period}</span>
          <X.ChevD s={13} />
        </button>
        {pOpen && (
          <div className="k2-pop" style={{ top: 38, right: 0, minWidth: 200 }} role="menu">
            <div className="k2-hd">{t('chrome.period')}</div>
            {options.map((p) => (
              <div
                key={p}
                role="menuitem"
                className={`k2-it${p === period ? ' k2-on' : ''}`}
                onClick={() => { setPeriod(p); setPOpen(false); }}
              >
                {p}
                {p === period && <span className="k2-ck"><X.Check s={13} /></span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }} ref={uRef}>
        <button type="button" className="k2-av" onClick={() => setUOpen((v) => !v)} aria-label={t('chrome.account')}>
          {initials}
        </button>
        {uOpen && (
          <div className="k2-pop" style={{ top: 36, right: 0, minWidth: 226 }} role="menu">
            <div style={{ padding: '8px 9px 10px' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{session.username || t('chrome.account')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--n500)' }}>
                {email}{plan ? ` · ${plan}` : ''}
              </div>
              {isAdmin && (
                <div style={{ marginTop: 6 }}>
                  <span className="k2-g k2-g-dark">{isOwner ? t('chrome.owner') : t('chrome.admin')}</span>
                </div>
              )}
            </div>
            <div className="k2-hr" />
            <div className="k2-it" role="menuitem" onClick={() => { setUOpen(false); navigate('/account'); }}>
              <X.Gear s={14} />{t('chrome.accountPassword')}
            </div>
            <div className="k2-it" role="menuitem" onClick={() => { setUOpen(false); navigate('/buildings'); }}>
              <X.Building s={14} />{t('nav.facilities')}
            </div>
            {/* Owner/admin console — kept out of the sidebar so ordinary navigation
                looks identical for every account. Server-side role checks are the
                actual boundary; this only decides what is shown. */}
            {isAdmin && (
              <>
                <div className="k2-hr" />
                <div className="k2-hd">{isOwner ? t('chrome.owner') : t('chrome.admin')}</div>
                <div className="k2-it" role="menuitem" onClick={() => { setUOpen(false); navigate('/admin/users'); }}>
                  <X.Users s={14} />{t('nav.admin.users')}
                </div>
                <div className="k2-it" role="menuitem" onClick={() => { setUOpen(false); navigate('/admin/tokens'); }}>
                  <X.Shield s={14} />{t('nav.admin.tokens')}
                </div>
              </>
            )}
            <div className="k2-hr" />
            <div className="k2-hd">{t('chrome.language')}</div>
            {LANGUAGES.map((l) => (
              <div
                key={l.code}
                role="menuitemradio"
                aria-checked={l.code === lang}
                className={`k2-it${l.code === lang ? ' k2-on' : ''}`}
                onClick={() => { setLang(l.code); setUOpen(false); }}
              >
                <span className="k2-lang">{l.short}</span>
                {l.label}
                {l.code === lang && <span className="k2-ck"><X.Check s={13} /></span>}
              </div>
            ))}
            <div className="k2-hr" />
            <div className="k2-it" role="menuitem" onClick={logout}>
              <X.Log s={14} />{t('chrome.signOut')}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
